import React, { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/utils-tg";
import { Loader2, UserPlus, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const { register, user, logout } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const invitedEmail = (params.get("email") || "").trim().toLowerCase();
  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Already signed in:
  //  - no invite param → nothing to do here, send them home.
  //  - invite is for the SAME account they're in → already joined, send home.
  //  - invite is for a DIFFERENT email → let them sign out to accept it.
  if (user && !invitedEmail) return <Navigate to="/" replace />;
  if (user && invitedEmail && user.email?.toLowerCase() === invitedEmail) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success("Welcome to Talk & Go!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  // Signed in as someone else but landed on an invite for a different email.
  if (user && invitedEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="register-page">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <img src={LOGO_URL} className="h-20 w-auto object-contain" alt="Talk & Go" />
          </div>
          <div className="tg-card p-8 text-center">
            <UserPlus className="h-8 w-8 text-emerald-600 mx-auto mb-3" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Accept your invite</h1>
            <p className="text-sm text-slate-600 mt-3">
              You're currently signed in as{" "}
              <span className="font-semibold">{user.email}</span>. This invite is for{" "}
              <span className="font-mono text-xs">{invitedEmail}</span>.
            </p>
            <p className="text-sm text-slate-500 mt-2">
              Sign out to create the new account and join the workspace.
            </p>
            <button
              onClick={() => logout()}
              className="tg-btn-primary w-full mt-6"
              data-testid="register-signout-to-accept"
            >
              <LogOut className="h-4 w-4" /> Sign out & accept invite
            </button>
            <button
              onClick={() => nav("/")}
              className="text-sm text-slate-500 hover:underline mt-4"
              data-testid="register-stay-signed-in"
            >
              Stay signed in as {user.email}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <img src={LOGO_URL} className="h-20 w-auto object-contain" alt="Talk & Go" />
        </div>

        <div className="tg-card p-8">
          {invitedEmail ? (
            <div className="flex items-start gap-2.5 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 mb-5" data-testid="invite-banner">
              <UserPlus className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-700">
                <span className="font-semibold">You're invited.</span> Create your account with{" "}
                <span className="font-mono text-xs">{invitedEmail}</span> and you'll land straight in your team's workspace.
              </div>
            </div>
          ) : null}
          <h1 className="font-display text-2xl font-bold text-slate-900">{invitedEmail ? "Accept invite" : "Create your workspace"}</h1>
          <p className="text-sm text-slate-500 mt-1 mb-6">A few details and you're in.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Name</label>
              <input
                required
                className="tg-input mt-1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Martin"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <input
                type="email"
                required
                readOnly={!!invitedEmail}
                className={`tg-input mt-1 ${invitedEmail ? "bg-slate-50 cursor-not-allowed" : ""}`}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alex@company.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <input
                type="password"
                required
                minLength={6}
                className="tg-input mt-1"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            <button type="submit" disabled={busy} className="tg-btn-primary w-full" data-testid="register-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
            </button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald-600 font-semibold hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
